import { useEffect, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { PRODUCT_NAME } from '@studio/contracts';
import { supabase } from '@/lib/supabase';
import { trpc } from '@/lib/trpc';
import { Icon } from '@/components/icons';
import { GoogleMark, Mast } from '@/components/sign-in-card';

type Step = 'email' | 'code';

/** Supabase refuses a code for an unknown address on this door; the sign-up door is where accounts are made. */
const NO_ACCOUNT_MARK = 'Signups not allowed';

/**
 * design/canvas/Login.src.html: one door for creators, editors and reviewers; the role decides what
 * comes after. An invitee arrives with ?invite= (greeted with the brand, address filled in) or, once
 * they already had an account, with ?email=.
 */
export function SignInPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/sign-in' });
  const invite = useQuery({ ...trpc.team.peek.queryOptions({ inviteId: search.invite ?? '' }), enabled: !!search.invite });
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState(search.email ?? '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [usePassword, setUsePassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (invite.data?.email && !email) setEmail(invite.data.email);
  }, [invite.data, email]);

  const google = async (): Promise<void> => {
    setError(null);
    const { error: err } = await supabase().auth.signInWithOAuth({ provider: 'google', options: { redirectTo: `${location.origin}/auth/callback` } });
    if (err) setError(err.message);
  };

  const sendCode = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await supabase().auth.signInWithOtp({ email, options: { shouldCreateUser: false, emailRedirectTo: `${location.origin}/auth/callback` } });
    setBusy(false);
    if (err) setError(err.message.includes(NO_ACCOUNT_MARK) ? 'No account uses that address yet. Create one below.' : err.message);
    else setStep('code');
  };

  const signInPassword = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await supabase().auth.signInWithPassword({ email, password });
    setBusy(false);
    if (err) setError(err.message);
    else await navigate({ to: '/' });
  };

  const verify = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await supabase().auth.verifyOtp({ email, token: code, type: 'email' });
    setBusy(false);
    if (err) setError(err.message);
    else await navigate({ to: '/' });
  };

  const joining = invite.data?.valid ? invite.data.brandName : null;

  return (
    <main className="signin">
      <div className="card">
        <Mast />
        <div>
          <h1>{joining ? `Join ${joining}` : 'Sign in'}</h1>
          <div className="lead">{joining ? `Sign in as ${invite.data!.email} and you are in.` : `Your ${PRODUCT_NAME} account.`}</div>
        </div>
        <button type="button" className="btn lg" onClick={() => void google()}>
          <GoogleMark />
          Continue with Google
        </button>
        <div className="divider">or</div>
        {step === 'email' ? (
          <form className="stack" onSubmit={(e) => void (usePassword ? signInPassword(e) : sendCode(e))}>
            <label className="field lg">
              <input type="email" required autoComplete="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email address" />
            </label>
            {usePassword && (
              <label className="field lg">
                <input type="password" required autoComplete="current-password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} aria-label="Password" />
              </label>
            )}
            <button type="submit" className="btn lg primary" disabled={busy || !email || (usePassword && !password)}>
              {usePassword ? 'Sign in' : 'Email me a sign-in code'}
            </button>
            <button type="button" className="btn quiet" onClick={() => { setUsePassword(!usePassword); setError(null); }}>
              {usePassword ? 'Email me a code instead' : 'Use a password instead'}
            </button>
          </form>
        ) : (
          <CodeForm email={email} code={code} setCode={setCode} busy={busy} onSubmit={verify} onBack={() => setStep('email')} />
        )}
        {error && <div className="error" role="alert">{error}</div>}
        <p className="note">New here? <Link to="/sign-up">Create an account</Link></p>
      </div>
    </main>
  );
}

/** The six-to-ten digit code from the email; the link in the same email also signs this device in. */
export function CodeForm({ email, code, setCode, busy, onSubmit, onBack }: { email: string; code: string; setCode: (c: string) => void; busy: boolean; onSubmit: (e: FormEvent) => Promise<void>; onBack: () => void }) {
  return (
    <form className="stack" onSubmit={(e) => void onSubmit(e)}>
      <div className="note">Sent to <b>{email}</b>. Enter the code from the email, or open its link on this device.</div>
      <label className="field lg">
        <input className="code" inputMode="numeric" pattern="[0-9]{6,10}" maxLength={10} required autoFocus placeholder="00000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} aria-label="Sign-in code" />
      </label>
      <button type="submit" className="btn lg primary" disabled={busy || code.length < 6}>Continue</button>
      <button type="button" className="btn quiet" onClick={onBack}><Icon name="lines" />Use a different email</button>
    </form>
  );
}
